import { DELETE, GET, PATCH, POST } from '@/shared/api/requests';
import type {
  DailyLog,
  DailyLogAttachment,
  ManpowerEntry,
  ManpowerEntryDraft,
  NoteEntry,
  NoteEntryDraft,
  SignedUploadResponse,
} from '../types';

const BASE = '/daily-log';

export const getDailyLogForDate = async (
  projectId: number,
  date: string,
): Promise<DailyLog> => {
  const params = new URLSearchParams({
    project_id: String(projectId),
    date,
  });
  const response = await GET(`${BASE}/daily-logs/for-date/?${params.toString()}`);
  return response.data as DailyLog;
};

export const createManpowerEntry = async (
  dailyLogId: string,
  draft: ManpowerEntryDraft,
): Promise<ManpowerEntry> => {
  const response = await POST(`${BASE}/manpower-entries/`, {
    daily_log: dailyLogId,
    ...draft,
  });
  return response.data as ManpowerEntry;
};

export const updateManpowerEntry = async (
  id: string,
  patch: Partial<ManpowerEntryDraft>,
): Promise<ManpowerEntry> => {
  const response = await PATCH(`${BASE}/manpower-entries/${id}/`, patch);
  return response.data as ManpowerEntry;
};

export const deleteManpowerEntry = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/manpower-entries/${id}/`);
};

export const createNoteEntry = async (
  dailyLogId: string,
  draft: NoteEntryDraft,
): Promise<NoteEntry> => {
  const response = await POST(`${BASE}/note-entries/`, {
    daily_log: dailyLogId,
    ...draft,
  });
  return response.data as NoteEntry;
};

export const updateNoteEntry = async (
  id: string,
  patch: Partial<NoteEntryDraft>,
): Promise<NoteEntry> => {
  const response = await PATCH(`${BASE}/note-entries/${id}/`, patch);
  return response.data as NoteEntry;
};

export const deleteNoteEntry = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/note-entries/${id}/`);
};

export const requestAttachmentUploadUrl = async (payload: {
  project_id: number;
  file_name: string;
  content_type: string;
}): Promise<SignedUploadResponse> => {
  const response = await POST(`${BASE}/attachments/upload-url/`, payload);
  return response.data as SignedUploadResponse;
};

export const finalizeAttachment = async (payload: {
  project_id: number;
  manpower_entry_id?: string | null;
  note_entry_id?: string | null;
  file_uuid: string;
  gcs_object_path: string;
  original_file_name: string;
  mime_type?: string;
  file_size?: number;
}): Promise<DailyLogAttachment> => {
  const response = await POST(`${BASE}/attachments/finalize/`, payload);
  return response.data as DailyLogAttachment;
};

export const deleteAttachment = async (uuid: string): Promise<void> => {
  await DELETE(`${BASE}/attachments/${uuid}/`);
};

export const uploadFileToSignedUrl = async (
  file: File,
  signedUrl: string,
  onProgress?: (progressPct: number) => void,
): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (!onProgress || !event.lengthComputable) return;
      onProgress((event.loaded / event.total) * 100);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload failed with status ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed.')));

    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
};
